import { Role, SerializedToken, logout as authLogout, API_URL } from "@/api/auth";
import { create } from "zustand";
import {
  type OnboardingProgress,
  FUNDING_SOURCES_REQUIRING_ONBOARDING,
} from "@/lib/config/onboarding";

interface AuthState {
  token_payload: SerializedToken | null;
  setTokenPayload: (payload: SerializedToken | null) => void;
  readFromLocalStorage: () => void;
  hydrateFromMe: () => Promise<boolean>;
  logout: () => void;
  getFullName: () => string;
  getEmail: () => string;
  getRole: () => Role;
  getUserId: () => string;
  getCompanyName: () => string;
  getCompanyId: () => string;
  getCompanyType: () => string;
  hasProperties: () => boolean;
  hasServices: () => boolean;
  isOnboardingComplete: () => boolean;
  getFundingSource: () => string;
  hasBankAccount: () => boolean;
  isPrimaryResident: () => boolean;
  getOnboardingProgress: () => OnboardingProgress | null;
  needsOnboarding: () => boolean;
  getCompanyCode: () => string;
  refreshState: () => void;
  shouldShowArrears: () => boolean;
  hasArrearsBalance: () => boolean;
  hasArrearsConsent: () => boolean;
  getCollectArrearAmount: () => number;
  getCollectArrearOriginalAmount: () => number;
  getCollectArrearMode: () => "one_time" | "max_500" | "max_1000";
}

export const useAuthStore = create<AuthState>()((set, get) => ({
  token_payload: null,
  setTokenPayload: (payload) => set({ token_payload: payload }),
  readFromLocalStorage: () => {
    // VAPT #15: identity no longer lives in localStorage. If we don't already
    // have it in memory, pull it from the cookie-authenticated /me endpoint.
    // Never clobber existing state (callers use this defensively on mount).
    if (typeof window === "undefined") return;
    if (!get().token_payload) {
      void get().hydrateFromMe();
    }
  },
  hydrateFromMe: async () => {
    if (typeof window === "undefined") return false;
    try {
      const res = await fetch(`${API_URL}/api/v1/me/`, {
        method: "GET",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
      });
      if (!res.ok) {
        set({ token_payload: null });
        try {
          localStorage.removeItem("airbills_signed_in");
        } catch {
          // ignore
        }
        return false;
      }
      const json = await res.json();
      // MeView wraps the identity in { success, data }.
      const data = (json && typeof json === "object" && "data" in json ? json.data : json) as any;
      // /me returns SimpleJWT `id`; keep `user_id` populated for callers that
      // expect the JWT shape.
      const payload = { ...data, user_id: data?.id } as SerializedToken;
      set({ token_payload: payload });
      try {
        localStorage.setItem("airbills_signed_in", "1");
      } catch {
        // ignore
      }
      return true;
    } catch {
      return false;
    }
  },
  logout: () => {
    // Call the auth logout function which will clear localStorage and sessionStorage
    authLogout();

    // Reset the store state
    set({ token_payload: null });

    // Redirect to login page after logout
    if (typeof window !== "undefined") {
      window.location.href = "/sign-in";
    }
  },
  getFullName: (): string => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return `${token_payload?.first_name} ${token_payload?.last_name}`;
  },
  getEmail: (): string => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return token_payload?.email || "";
  },
  getRole: (): Role => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return token_payload?.role as Role;
  },
  getUserId: (): string => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return token_payload?.id ? String(token_payload.id) : "";
  },
  getCompanyName: (): string => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return token_payload?.company_name || "";
  },
  getCompanyId: (): string => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();

    // First try the specific company_id field
    if (token_payload?.company_id) {
      return token_payload.company_id;
    }

    // Try fallbacks if company_id is not available
    const companyIdFromPayload =
      token_payload?.company_id ||
      token_payload?.companyId ||
      token_payload?.["company-id"] ||
      token_payload?.company?.id;

    if (companyIdFromPayload) {
      return companyIdFromPayload;
    }

    return "";
  },
  getCompanyType: (): string => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return token_payload?.company_type.toLowerCase() || "b2b";
  },
  hasProperties: (): boolean => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return !!token_payload?.has_properties;
  },
  hasServices: (): boolean => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return !!token_payload?.has_services;
  },
  isOnboardingComplete: (): boolean => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return !!token_payload?.has_properties && !!token_payload?.has_services;
  },
  getFundingSource: (): string => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return token_payload?.funding_source || "company_only";
  },
  hasBankAccount: (): boolean => {
    const { token_payload }: { token_payload: SerializedToken | null } =
      useAuthStore.getState();
    return token_payload?.has_bank_account !== false;
  },
  isPrimaryResident: (): boolean => {
    const { token_payload } = useAuthStore.getState();
    return token_payload?.is_primary_resident === true;
  },
  getOnboardingProgress: (): OnboardingProgress | null => {
    const { token_payload } = useAuthStore.getState();
    const progress = token_payload?.onboarding_progress;
    if (
      progress &&
      typeof progress === "object" &&
      !Array.isArray(progress) &&
      "completed" in progress
    ) {
      return progress as OnboardingProgress;
    }
    return null;
  },
  needsOnboarding: (): boolean => {
    // Pay Now pivot (May 2026) — bank-link onboarding is no longer a
    // hard gate. Residents land on the Dashboard regardless of bank-link
    // state; the persistent `<AutopayBanner />` (rendered in
    // `app/dashboard/layout.tsx`) handles the "no bank yet" and
    // "bank-linked, autopay off" cases as non-blocking prompts.
    //
    // We keep this function (and the underlying onboarding_progress
    // schema) so legacy callers and admin tooling can still inspect
    // step completion — they just shouldn't gate on it anymore.
    return false;
  },
  getCompanyCode: (): string => {
    const { token_payload } = useAuthStore.getState();
    return token_payload?.company_code || "";
  },
  shouldShowArrears: (): boolean => {
    const { token_payload } = useAuthStore.getState();
    if (!token_payload) return false;
    if (token_payload.role !== "resident") return false;
    if (token_payload.is_primary_resident !== true) return false;
    if (token_payload.has_bank_account === false) return false;
    if (!token_payload.collect_arrears) return false;
    if (!token_payload.collect_arrear_amount || token_payload.collect_arrear_amount <= 0) return false;
    if (token_payload.has_arrears_consent === true) return false;
    return true;
  },
  hasArrearsBalance: (): boolean => {
    const { token_payload } = useAuthStore.getState();
    if (!token_payload) return false;
    if (!token_payload.collect_arrears) return false;
    if (!token_payload.collect_arrear_amount || token_payload.collect_arrear_amount <= 0) return false;
    return true;
  },
  hasArrearsConsent: (): boolean => {
    const { token_payload } = useAuthStore.getState();
    return !!token_payload?.has_arrears_consent;
  },
  getCollectArrearAmount: (): number => {
    const { token_payload } = useAuthStore.getState();
    return token_payload?.collect_arrear_amount ?? 0;
  },
  getCollectArrearOriginalAmount: (): number => {
    const { token_payload } = useAuthStore.getState();
    return token_payload?.collect_arrear_original_amount ?? 0;
  },
  getCollectArrearMode: (): "one_time" | "max_500" | "max_1000" => {
    const { token_payload } = useAuthStore.getState();
    return token_payload?.collect_arrear_mode ?? "one_time";
  },
  refreshState: () => {
    // VAPT #15: re-pull identity from /me (fire-and-forget). NOTE: /me is
    // thinner than the old JWT payload, so onboarding-only flags are not
    // refreshed by this call.
    if (typeof window !== "undefined") {
      void get().hydrateFromMe();
    }
  },
}));

// Initialize auth state on load - hydrate identity from the cookie-authenticated
// /me endpoint (VAPT #15: nothing is read from localStorage anymore).
if (typeof window !== "undefined") {
  // VAPT #15 migration cleanup: scrub any legacy auth material left in
  // localStorage by the pre-cookie build. Existing users still carry these keys
  // from before the migration; auth now lives in HttpOnly cookies and these must
  // never be read again. Idempotent and cheap (no-op once already clear).
  try {
    for (const k of [
      "access_token",
      "refresh_token",
      "token_payload",
      "company_id",
      "impersonator_access_token",
      "impersonator_refresh_token",
      "impersonator_company_id",
      "impersonator_token_payload",
    ]) {
      localStorage.removeItem(k);
    }
  } catch {
    // Storage can throw in private-mode/quota edge cases; cleanup is best-effort.
  }

  useAuthStore.getState().readFromLocalStorage();

  // Cross-tab sync: key off the lightweight `airbills_signed_in` flag (set on a
  // successful /me hydrate, cleared on logout) instead of the removed
  // `token_payload`. On sign-in elsewhere we re-hydrate; on sign-out we clear.
  let storageTimeout: NodeJS.Timeout | null = null;
  window.addEventListener("storage", (event) => {
    if (event.key !== "airbills_signed_in") return;
    if (storageTimeout) clearTimeout(storageTimeout);
    storageTimeout = setTimeout(() => {
      const currentState = useAuthStore.getState();
      if (event.newValue) {
        void currentState.hydrateFromMe();
      } else {
        currentState.setTokenPayload(null);
      }
      storageTimeout = null;
    }, 100); // 100ms debounce
  });
}
